import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import Check from "@mui/icons-material/Check";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import NotificationsNone from "@mui/icons-material/NotificationsNone";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { orange } from "@mui/material/colors";
import { alpha, keyframes } from "@mui/material/styles";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import {
  GET_NOTIFICATION_USER,
  GET_MY_NOTIFICATIONS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  ARCHIVE_NOTIFICATION,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_ALL_NOTIFICATIONS_UNREAD,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_UNREAD,
} from "../../hooks/useNotifications";
import { useOakStream } from "../../stream";
import { useAlerts } from "../Alerts/AlertProvider";
import { NotificationLiveStatus } from "./NotificationLiveStatus";
import {
  getNotificationDestination,
  NOTIFICATIONS_LIST_URL,
  notificationPreferencesUrl,
  type NotificationOpenTarget,
} from "./notificationNavigation";
import { NotificationTypeIcon } from "./NotificationTypeIcon";
import { NotificationUnreadIndicator } from "./NotificationVisualIndicators";

function getRelativeTime(value: string) {
  return DateTime.fromISO(value).toRelative({ base: DateTime.now() }) ?? "";
}

interface NotificationChangedPayload {
  notificationUserId: string | null;
  tenantId: string | null;
  reason: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNotificationChangedPayload(value: unknown): value is NotificationChangedPayload {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNullableString(value.notificationUserId) &&
    isNullableString(value.tenantId) &&
    typeof value.reason === "string"
  );
}

const bellBounce = keyframes`
  0% {
    transform: translateY(0) scale(1);
  }
  32% {
    transform: translateY(-2px) scale(1.06);
  }
  64% {
    transform: translateY(1px) scale(0.98);
  }
  100% {
    transform: translateY(0) scale(1);
  }
`;

const NAV_ACTION_SIZE = 40;

export function NotificationBell() {
  const { featureFlags } = useFeatureFlags();
  // Preferences are per organization, so the link needs the current one. Absent
  // only before a tenant is selected, where the link would go nowhere useful and is
  // therefore not offered.
  const { tenantId } = useTenant();
  const isNotificationsEnabled = featureFlags[FeatureFlagKey.Notifications];
  const navigate = useNavigate();
  const alerts = useAlerts();
  const apolloClient = useApolloClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isBellBouncing, setIsBellBouncing] = useState(false);
  const [mutatingNotificationId, setMutatingNotificationId] = useState<string | null>(null);
  const [optimisticUnreadCount, setOptimisticUnreadCount] = useState<number | null>(null);
  const [topActionsAnchorEl, setTopActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [trayFilter, setTrayFilter] = useState<"unread" | "all">("all");
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [rowActionsNotificationUserId, setRowActionsNotificationUserId] = useState<string | null>(
    null
  );
  const bounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledStreamEventIdRef = useRef<string | null>(null);
  // The toast outlives the render that created it, so it reaches the current
  // open handler through a ref instead of capturing a stale closure.
  const openNotificationRef = useRef<(target: NotificationOpenTarget) => void>(() => {});
  const open = Boolean(anchorEl);

  const countQuery = useQuery(GET_MY_UNREAD_NOTIFICATION_COUNT, {
    skip: !isNotificationsEnabled,
    fetchPolicy: "cache-and-network",
  });
  const listQuery = useQuery(GET_MY_NOTIFICATIONS, {
    variables: {
      page: 0,
      pageSize: 8,
      includeArchived: false,
      onlyUnread: trayFilter === "unread",
    },
    skip: !isNotificationsEnabled || !open,
    fetchPolicy: "cache-and-network",
  });
  const [markRead] = useMutation(MARK_NOTIFICATION_READ);
  const [markUnread] = useMutation(MARK_NOTIFICATION_UNREAD);
  const [archiveNotification] = useMutation(ARCHIVE_NOTIFICATION);
  const [markAllRead, markAllReadState] = useMutation(MARK_ALL_NOTIFICATIONS_READ);
  const [markAllUnread, markAllUnreadState] = useMutation(MARK_ALL_NOTIFICATIONS_UNREAD);

  const stream = useOakStream({
    url: "/events/notifications",
    enabled: isNotificationsEnabled,
  });
  const latestStreamEvent = useMemo(() => stream.data.at(-1), [stream.data]);

  const showNotificationToast = useCallback(
    async (notificationUserId: string) => {
      let title = "New notification";
      let target: NotificationOpenTarget = {
        id: notificationUserId,
        readAt: null,
        notification: {},
      };

      try {
        const result = await apolloClient.query({
          query: GET_NOTIFICATION_USER,
          variables: { id: notificationUserId },
          fetchPolicy: "network-only",
        });
        const notificationUser = result.data.notificationUser;
        if (notificationUser) {
          target = notificationUser;
          title = notificationUser.notification.title ?? title;
        }
      } catch {
        // The stream event is enough to route; the lookup only improves the toast copy.
      }

      alerts.info(
        <ButtonBase
          onClick={() => openNotificationRef.current(target)}
          sx={{
            color: "inherit",
            display: "block",
            maxWidth: 320,
            textAlign: "left",
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
            {title}
          </Typography>
        </ButtonBase>,
        { autoHideDuration: 8000 }
      );
    },
    [alerts, apolloClient]
  );

  const bounceBell = useCallback(() => {
    if (bounceTimeoutRef.current) {
      clearTimeout(bounceTimeoutRef.current);
    }

    setIsBellBouncing(true);
    bounceTimeoutRef.current = setTimeout(() => {
      setIsBellBouncing(false);
      bounceTimeoutRef.current = null;
    }, 520);
  }, []);

  useEffect(() => {
    return () => {
      if (bounceTimeoutRef.current) {
        clearTimeout(bounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!latestStreamEvent || latestStreamEvent.id === handledStreamEventIdRef.current) {
      return;
    }

    const notificationChange = isNotificationChangedPayload(latestStreamEvent.data)
      ? latestStreamEvent.data
      : null;
    const isCreated =
      latestStreamEvent.event === "notification.changed" &&
      notificationChange?.reason === "created";

    handledStreamEventIdRef.current = latestStreamEvent.id;
    if (latestStreamEvent.event === "notification.changed") {
      setOptimisticUnreadCount(null);
    }
    void countQuery.refetch();
    if (open) {
      void listQuery.refetch();
    }

    if (!isCreated) {
      return;
    }

    const notificationUserId = notificationChange?.notificationUserId;
    if (notificationUserId === null || notificationUserId === undefined) {
      return;
    }

    bounceBell();
    void showNotificationToast(notificationUserId);
  }, [bounceBell, countQuery, latestStreamEvent, listQuery, open, showNotificationToast]);

  const notifications = useMemo(
    () => listQuery.data?.myNotifications.items ?? [],
    [listQuery.data?.myNotifications.items]
  );
  const isInitialNotificationsLoading = listQuery.loading && notifications.length === 0;
  const isRefreshingNotifications = listQuery.loading && notifications.length > 0;
  const serverUnreadCount = countQuery.data?.myUnreadNotificationCount ?? 0;
  const unreadCount = optimisticUnreadCount ?? serverUnreadCount;
  const rowActionsNotificationUser = useMemo(
    () =>
      notifications.find(
        (notificationUser) => notificationUser.id === rowActionsNotificationUserId
      ) ?? null,
    [notifications, rowActionsNotificationUserId]
  );

  useEffect(() => {
    if (serverUnreadCount === 0) {
      setOptimisticUnreadCount(null);
    }
  }, [serverUnreadCount]);

  useEffect(() => {
    const handleFocus = () => {
      setOptimisticUnreadCount(null);
      void countQuery.refetch();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [countQuery]);

  const refresh = async () => {
    await countQuery.refetch();
    if (open) {
      await listQuery.refetch();
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    void countQuery.refetch();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTopActionsAnchorEl(null);
    setRowActionsAnchorEl(null);
    setRowActionsNotificationUserId(null);
  };

  const handleTopActionsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTopActionsAnchorEl(event.currentTarget);
  };

  const handleTopActionsClose = () => {
    setTopActionsAnchorEl(null);
  };

  const handleRowActionsOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationUserId: string
  ) => {
    event.stopPropagation();
    setRowActionsAnchorEl(event.currentTarget);
    setRowActionsNotificationUserId(notificationUserId);
  };

  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null);
    setRowActionsNotificationUserId(null);
  };

  const handleMarkAllRead = () => {
    setOptimisticUnreadCount(0);
    handleClose();

    void (async () => {
      try {
        await markAllRead({ variables: {} });
        await refresh();
      } catch {
        setOptimisticUnreadCount(null);
      }
    })();
  };

  const handleMarkAllUnread = () => {
    setOptimisticUnreadCount(null);
    handleClose();

    void (async () => {
      try {
        await markAllUnread({ variables: {} });
        await refresh();
      } catch {
        setOptimisticUnreadCount(null);
      }
    })();
  };

  const updateNotificationReceipt = async (
    notificationUserId: string,
    mutation: () => Promise<unknown>
  ) => {
    setMutatingNotificationId(notificationUserId);

    try {
      await mutation();
    } finally {
      setMutatingNotificationId((currentId) =>
        currentId === notificationUserId ? null : currentId
      );
      void refresh();
    }
  };

  const adjustOptimisticUnreadCount = (delta: number) => {
    setOptimisticUnreadCount((previous) => {
      const base = previous ?? serverUnreadCount;
      return Math.max(0, base + delta);
    });
  };

  const handleToggleNotificationRead = () => {
    const notificationUserId = rowActionsNotificationUser?.id;
    if (!notificationUserId) {
      return;
    }

    handleRowActionsClose();
    const wasRead = Boolean(rowActionsNotificationUser.readAt);
    const nowIso = new Date().toISOString();
    adjustOptimisticUnreadCount(wasRead ? 1 : -1);
    void updateNotificationReceipt(notificationUserId, () => {
      if (wasRead) {
        return markUnread({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationUnread: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: null,
            },
          },
        });
      }

      return markRead({
        variables: { notificationUserId },
        optimisticResponse: {
          markNotificationRead: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: nowIso,
          },
        },
      });
    });
  };

  const handleArchiveNotification = () => {
    const notificationUserId = rowActionsNotificationUser?.id;
    if (!notificationUserId) {
      return;
    }

    handleRowActionsClose();
    const wasUnread = !rowActionsNotificationUser?.readAt;
    if (wasUnread) {
      adjustOptimisticUnreadCount(-1);
    }
    void updateNotificationReceipt(notificationUserId, () =>
      archiveNotification({
        variables: { notificationUserId },
        optimisticResponse: {
          archiveNotification: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: new Date().toISOString(),
            archivedAt: new Date().toISOString(),
          },
        },
      })
    );
  };

  // Opening a notification marks it read and navigates to its subject in one
  // action — there is no interstitial detail page to do the mark-read for us.
  const openNotification = (target: NotificationOpenTarget) => {
    const notificationUserId = target.id;
    if (!target.readAt) {
      adjustOptimisticUnreadCount(-1);
      void updateNotificationReceipt(notificationUserId, () =>
        markRead({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationRead: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: new Date().toISOString(),
            },
          },
        })
      );
    }
    handleClose();
    navigate(getNotificationDestination(target));
  };

  openNotificationRef.current = openNotification;

  if (!isNotificationsEnabled) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Tooltip title="Notifications">
          <IconButton
            aria-label="Notifications"
            onClick={handleOpen}
            size="small"
            sx={{
              width: NAV_ACTION_SIZE,
              height: NAV_ACTION_SIZE,
              p: 0,
              bgcolor: open ? orange[100] : "transparent",
              color: "action.active",
              transition: "background-color 150ms ease",
              animation: isBellBouncing
                ? `${bellBounce} 520ms cubic-bezier(0.2, 0.8, 0.2, 1)`
                : "none",
              "&:hover": {
                bgcolor: open ? orange[200] : orange[50],
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {unreadCount > 0 ? (
              <Badge
                variant="dot"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: orange[400],
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.background.paper}`,
                    height: 10,
                    minWidth: 10,
                    right: 8,
                    top: 8,
                  },
                }}
              >
                <NotificationsNone sx={{ fontSize: 24 }} />
              </Badge>
            ) : (
              <NotificationsNone sx={{ fontSize: 24 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 420, maxWidth: "calc(100vw - 24px)" },
          },
        }}
      >
        <Stack spacing={0} sx={{ maxHeight: 560 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pl: 2, pr: 1, py: 1.25 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography variant="subtitle1" fontWeight={700}>
                Notifications
              </Typography>
              <ToggleButtonGroup
                value={trayFilter}
                exclusive
                size="small"
                onChange={(_event, value: "unread" | "all" | null) => {
                  if (value !== null) setTrayFilter(value);
                }}
                aria-label="Filter notifications"
                sx={{
                  "& .MuiToggleButton-root": {
                    border: 0,
                    borderRadius: 999,
                    px: 1.25,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                    color: "text.secondary",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: "primary.main",
                  },
                }}
              >
                <ToggleButton value="all" aria-label="Show all notifications">
                  All
                </ToggleButton>
                <ToggleButton value="unread" aria-label="Show only unread notifications">
                  Unread
                </ToggleButton>
              </ToggleButtonGroup>
              {isRefreshingNotifications ? (
                <CircularProgress
                  aria-label="Refreshing notifications"
                  data-testid="notifications-tray-title-loading"
                  size={13}
                  thickness={5}
                  sx={{ color: "text.disabled" }}
                />
              ) : null}
            </Stack>
            <Tooltip title="Notification actions">
              <IconButton
                aria-label="Notification actions"
                size="small"
                onClick={handleTopActionsOpen}
                sx={{
                  color: "text.secondary",
                  height: 32,
                  width: 32,
                }}
              >
                <MoreHoriz />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider />
          <Box sx={{ overflow: "auto", minHeight: 120 }}>
            {isInitialNotificationsLoading ? (
              <Stack
                alignItems="center"
                data-testid="notifications-tray-body-loading"
                justifyContent="center"
                sx={{ py: 4 }}
              >
                <CircularProgress size={24} />
              </Stack>
            ) : null}
            {!isInitialNotificationsLoading && notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {trayFilter === "unread" ? "No unread notifications" : "No notifications"}
              </Typography>
            ) : null}
            <List disablePadding>
              {notifications.map((notificationUser) => {
                const isUnread = !notificationUser.readAt;

                return (
                  <ListItemButton
                    key={notificationUser.id}
                    alignItems="flex-start"
                    onClick={() => openNotification(notificationUser)}
                    sx={{
                      bgcolor: (theme) =>
                        isUnread ? alpha(theme.palette.primary.main, 0.035) : "transparent",
                      minHeight: 60,
                      px: 2,
                      py: 1.1,
                      transition: "background-color 120ms ease",
                      "&:focus-within .notification-row-actions": {
                        opacity: 1,
                      },
                      "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.055),
                      },
                      "&:hover .notification-row-actions": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      sx={{ minWidth: 0, width: "100%" }}
                    >
                      <NotificationTypeIcon
                        category={notificationUser.notification.category}
                        programArea={notificationUser.notification.programArea ?? null}
                      />
                      <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isUnread ? "text.primary" : "text.secondary",
                            fontWeight: isUnread ? 700 : 500,
                            lineHeight: 1.35,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {notificationUser.notification.title}
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          sx={{ minWidth: 0 }}
                        >
                          {notificationUser.notification.facility?.name ? (
                            <>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{
                                  lineHeight: 1.3,
                                  fontWeight: 400,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  minWidth: 0,
                                }}
                              >
                                {notificationUser.notification.facility.name}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: "text.disabled",
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  height: 3,
                                  width: 3,
                                }}
                              />
                            </>
                          ) : null}
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ lineHeight: 1.3, flexShrink: 0 }}
                          >
                            {getRelativeTime(notificationUser.notification.createdAt)}
                          </Typography>
                          <NotificationLiveStatus
                            liveStatus={notificationUser.notification.subject?.liveStatus}
                          />
                        </Stack>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexShrink={0}
                        sx={{ minHeight: 28, pt: 0.25 }}
                      >
                        {isUnread ? <NotificationUnreadIndicator unread /> : null}
                        <Tooltip title="Notification actions">
                          <IconButton
                            aria-label={`Notification actions for ${notificationUser.notification.title}`}
                            className="notification-row-actions"
                            size="small"
                            onClick={(event) => handleRowActionsOpen(event, notificationUser.id)}
                            sx={{
                              color: "text.secondary",
                              height: 28,
                              opacity: rowActionsNotificationUserId === notificationUser.id ? 1 : 0,
                              transition: "opacity 120ms ease",
                              width: 28,
                              "&:hover": {
                                bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08),
                              },
                            }}
                          >
                            <MoreHoriz fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
          <Divider />
          {/*
            Two footer actions, opposite ends of a row that already existed: "Show
            all" goes to the inbox, and settings is the other thing a reader wants
            from here. Put in the footer rather than the kebab because it is
            navigation like its neighbour, not a bulk action on the notifications
            themselves, and because a link nobody has to open a menu to find is one
            fewer question for support.
          */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              size="small"
              onClick={() => {
                handleClose();
                navigate(NOTIFICATIONS_LIST_URL);
              }}
              sx={{
                fontWeight: 700,
                px: 2,
                py: 1.5,
                textTransform: "uppercase",
              }}
            >
              Show all
            </Button>
            {tenantId ? (
              <Button
                size="small"
                startIcon={<SettingsOutlined />}
                onClick={() => {
                  handleClose();
                  navigate(notificationPreferencesUrl(tenantId));
                }}
                sx={{ px: 2, py: 1.5 }}
              >
                Settings
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Popover>
      <Menu
        anchorEl={topActionsAnchorEl}
        open={Boolean(topActionsAnchorEl)}
        onClose={handleTopActionsClose}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <MenuItem
          disabled={unreadCount === 0 || markAllReadState.loading}
          onClick={handleMarkAllRead}
        >
          <ListItemIcon>
            <Check fontSize="small" />
          </ListItemIcon>
          Mark all as read
        </MenuItem>
        <MenuItem
          disabled={notifications.length === 0 || markAllUnreadState.loading}
          onClick={handleMarkAllUnread}
        >
          <ListItemIcon>
            <NotificationsActive fontSize="small" />
          </ListItemIcon>
          Mark all as unread
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={rowActionsAnchorEl}
        open={Boolean(rowActionsAnchorEl)}
        onClose={handleRowActionsClose}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <MenuItem
          disabled={
            !rowActionsNotificationUser || mutatingNotificationId === rowActionsNotificationUser.id
          }
          onClick={handleToggleNotificationRead}
        >
          <ListItemIcon>
            {rowActionsNotificationUser?.readAt ? (
              <NotificationsActive fontSize="small" />
            ) : (
              <Check fontSize="small" />
            )}
          </ListItemIcon>
          {rowActionsNotificationUser?.readAt ? "Mark as unread" : "Mark as read"}
        </MenuItem>
        <MenuItem
          disabled={
            !rowActionsNotificationUser || mutatingNotificationId === rowActionsNotificationUser?.id
          }
          onClick={handleArchiveNotification}
        >
          <ListItemIcon>
            <ArchiveOutlined fontSize="small" />
          </ListItemIcon>
          Archive
        </MenuItem>
      </Menu>
    </>
  );
}
